import { Component } from '@theme/component';

/** Keeps independent product panels usable and opens the existing request forms. */
class EmProductScrollControls extends Component {
  #abort;
  #observer;
  #frame;
  #root;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #setup() {
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#root = this.closest('.shopify-section')?.querySelector('.em-product-split');
    if (!this.#root) return;
    const panels = [
      [this.#root.querySelector('.product-information__media'), this.dataset.imagesLabel],
      [this.#root.querySelector('.product-details > .group-block'), this.dataset.detailsLabel],
    ];
    for (const [panel, label] of panels) {
      if (!panel) continue;
      panel.setAttribute('tabindex', '0');
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-label', label);
    }
    const schedule = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#resize());
    };
    window.addEventListener('resize', schedule, { signal, passive: true });
    window.addEventListener('scroll', schedule, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', schedule, { signal, passive: true });
    const header = document.querySelector('#header-group');
    if (header && 'ResizeObserver' in window) {
      this.#observer = new ResizeObserver(schedule);
      this.#observer.observe(header);
      const detailsContent = this.#root.querySelector('.product-details > .group-block');
      if (detailsContent) this.#observer.observe(detailsContent);
    }
    this.addEventListener('click', event => this.#openRequest(event), { signal });
    for (const button of this.querySelectorAll('[data-em-request]')) {
      const details = this.#root.querySelector('.em-' + button.dataset.emRequest + '__details');
      if (!details) { button.hidden = true; continue; }
      details.id ||= 'EmRequest-' + this.dataset.sectionId + '-' + button.dataset.emRequest;
      button.setAttribute('aria-controls', details.id);
    }
    schedule();
  }

  #resize() {
    const content = this.#root?.querySelector('.product-details > .group-block');
    if (content) {
      const detailsHeight = Math.ceil(content.getBoundingClientRect().height) + 'px';
      if (this.#root.style.getPropertyValue('--em-product-details-height') !== detailsHeight) {
        this.#root.style.setProperty('--em-product-details-height', detailsHeight);
      }
    }
    const grid = this.#root?.querySelector('[data-product-grid-content]');
    if (!grid) return;
    const height = Math.max(280, window.innerHeight - Math.max(16, grid.getBoundingClientRect().top) - 20);
    const value = height + 'px';
    if (this.#root.style.getPropertyValue('--em-product-panel-height') !== value) {
      this.#root.style.setProperty('--em-product-panel-height', value);
    }
  }

  #openRequest(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-em-request]');
    if (!button || !this.contains(button)) return;
    const details = this.#root.querySelector('.em-' + button.dataset.emRequest + '__details');
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = true;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    details.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
    const summary = details.querySelector('summary');
    summary?.focus({ preventScroll: true });
  }
}

if (!customElements.get('em-product-scroll-controls')) {
  customElements.define('em-product-scroll-controls', EmProductScrollControls);
}
